import { validateNode } from './node/node.js';
import { validatePersistentMenuNode } from './system-nodes/persistent-menu.js';
import { validateErrorHandlerNode } from './system-nodes/error-handler.js';
import { PifFileSummary } from '../common/pif-file-summary.js';
import { FRONTEND_PROPERTY_NAME } from '../common/constants.js';
export function validatePifFile(pifFile, accept, services) {
    const pifFileSummary = new PifFileSummary(pifFile);
    // Call validateNode for all nodes
    Object.values(pifFileSummary.allNodesMap).forEach((nodeFields) => {
        validateNode(nodeFields, pifFileSummary, services, accept);
    });
    // If there were any nodes found with duplicate ids, show
    // error messages against those nodes.
    pifFileSummary.duplicateNodesById.forEach((nodeFields) => {
        const { refNode, idField } = nodeFields;
        accept('error', `This id for the node '${nodeFields.refNode.name}' is duplicated which is not allowed.`, {
            node: refNode,
            data: {
                id: idField?.id?.value,
                nodeNumber: refNode.name,
                propertyName: FRONTEND_PROPERTY_NAME.ID,
            },
        });
    });
    // If there were any nodes found with duplicate node numbers, show
    // error messages against those nodes.
    pifFileSummary.duplicateNodesByNodeNumber.forEach((nodeFields) => {
        const { refNode, idField } = nodeFields;
        accept('error', `This node number '${nodeFields.refNode.name}' is duplicated which is not allowed.`, {
            node: refNode,
            property: 'name',
            data: {
                id: idField?.id?.value,
                nodeNumber: refNode.name,
                propertyName: FRONTEND_PROPERTY_NAME.NODE_NUMBER,
            },
        });
    });
    // Ensure that we have a start node (numbered 1)
    if (!pifFileSummary.startNode) {
        accept('error', `No start node found in the solution.`, { node: pifFile });
    }
    // Ensure that we have an out_of_scope node present
    if (pifFileSummary.outOfScopeNodes.length === 0) {
        accept('error', `NLU enabled solutions must have an 'out_of_scope' node.`, {
            node: pifFile,
        });
    }
    // Ensure that we don't have more than one out_of_scope node present
    if (pifFileSummary.outOfScopeNodes.length > 1) {
        const nodeFieldsArray = Object.values(pifFileSummary.outOfScopeNodes);
        nodeFieldsArray.forEach((nodeFields, index) => {
            const { idField } = nodeFields;
            // Generate an error for each out_of_scope node except the
            // first out_of_scope node
            if (index !== 0) {
                accept('error', `NLU enabled solutions must have only one 'out_of_scope' node.`, {
                    node: nodeFields.refNode,
                    data: {
                        id: idField?.id?.value,
                        nodeNumber: nodeFields.refNode.name,
                    },
                });
            }
        });
    }
    // Get all the platformFlags and for each platformFlag, check
    // that it is not defined in more than one nodes.
    const platformFlags = pifFileSummary.getAllPlatformFlags();
    platformFlags.forEach((platformFlag) => {
        const nodeFieldsArray = pifFileSummary.platformFlagsMap[platformFlag];
        if (nodeFieldsArray.length > 1) {
            // Get the node number for the first node in which the
            // platformFlag was defined
            const firstNodeNumber = nodeFieldsArray[0].refNode.name;
            // For the rest of the nodes that contain this platformFlag, generate
            // an error that the platformFlag has already been defined
            const extraNodeFieldsForPlatformFlag = nodeFieldsArray.slice(1);
            extraNodeFieldsForPlatformFlag.forEach((nodeFields) => {
                const { refNode, idField } = nodeFields;
                accept('error', `The platformFlag '${platformFlag}' has already been defined in node number '${firstNodeNumber}'.`, {
                    node: refNode,
                    data: {
                        id: idField?.id?.value,
                        nodeNumber: refNode.name,
                        propertyName: FRONTEND_PROPERTY_NAME.PLATFORM_FLAG,
                    },
                });
            });
        }
    });
    // If we have any persistent_menu nodes, validate those
    pifFileSummary.persistentMenuNodes.forEach((persistentMenuNode) => {
        validatePersistentMenuNode(persistentMenuNode, accept);
    });
    // If we have any nodes with command handleBotErrorNodes, validate those
    pifFileSummary.handleBotErrorNodes.forEach((handleBotErrorNode) => {
        validateErrorHandlerNode(handleBotErrorNode, accept);
    });
    // Warn about any nodes that are unreachable from the start node
    /*
    pifFileSummary.unreachableNodes.forEach((nodeFields) => {
      const { idField, refNode } = nodeFields;
      accept(
        'warning',
        `Node ${refNode.name} is unreachable from the start node.`,
        {
          node: refNode,
          data: {
            id: idField?.id?.value,
            nodeNumber: refNode.name,
          },
        }
      );
    });
    */
    if (pifFileSummary.hasNodeGroups()) {
        // Verify that the start node has a group order of 1 within it's group
        if (pifFileSummary.startNode) {
            const { refNode, idField, groupNameField, groupOrderField } = pifFileSummary.startNode;
            if (groupNameField &&
                groupNameField.groupName.$type !== 'EmptyField' &&
                groupOrderField &&
                groupOrderField.groupOrder.$type !== 'EmptyField' &&
                groupOrderField.groupOrder.value !== '1') {
                accept('error', "The start node should have the group order set as 1 inside it's group.", {
                    node: refNode,
                    data: {
                        id: idField?.id?.value,
                        nodeNumber: refNode.name,
                        propertyName: FRONTEND_PROPERTY_NAME.GROUP_ORDER,
                    },
                });
            }
        }
        // If we have node groups defined in the solution, then for any nodes
        // that do not have groupName or groupOrder defined, generate an error.
        const nodesWithoutGroup = pifFileSummary.getNodesWithoutGroup();
        nodesWithoutGroup.forEach((nodeFields) => {
            const { idField, groupNameField, groupOrderField, refNode } = nodeFields;
            // Only generate an error here if both groupName and groupOrder are missing.
            // If only one of them is missing, then the node's validate-group would also
            // generate an error and generating an error here as well would result in
            // two errors to be generated.
            if ((!groupNameField || groupNameField.groupName.$type === 'EmptyField') &&
                (!groupOrderField || groupOrderField.groupOrder.$type === 'EmptyField')) {
                accept('error', `Node '${refNode.name}' does not have a group name/order defined.`, {
                    node: refNode,
                    data: {
                        id: idField?.id?.value,
                        nodeNumber: refNode.name,
                        propertyName: FRONTEND_PROPERTY_NAME.GROUP_NAME,
                    },
                });
            }
        });
        // For nodes that do have name and order defined, ensure that the
        // ordering is valid.
        const groupNames = pifFileSummary.getAllGroupNames();
        groupNames.forEach((groupName) => {
            // Get the sorted (incrementing group order) list of nodes for the group
            const nodeFieldsArray = pifFileSummary.getNodesForGroup(groupName);
            nodeFieldsArray.forEach((nodeFields, index) => {
                const { idField, groupOrderField, refNode } = nodeFields;
                const groupOrder = Number(groupOrderField?.groupOrder?.value);
                if (groupOrder !== index + 1) {
                    accept('error', `Unexpected group order '${groupOrder}' found for node '${refNode.name}' in group '${groupName}'. Should instead be '${index + 1}'.`, {
                        node: refNode,
                        data: {
                            id: idField?.id?.value,
                            nodeNumber: refNode.name,
                            propertyName: FRONTEND_PROPERTY_NAME.GROUP_ORDER,
                        },
                    });
                }
            });
        });
        // Also for each group, we want to ensure that apart from the first
        // node in the group, no other node has incoming connections from
        // nodes outside the group.
        // And apart from the last node in the group, no other node has outgoing
        // connections to nodes outside the group.
        groupNames.forEach((groupName) => {
            // Get the sorted (incrementing group order) list of nodes for the group
            const nodeFieldsArray = pifFileSummary.getNodesForGroup(groupName);
            nodeFieldsArray.forEach((nodeFields, index) => {
                const { idField, refNode } = nodeFields;
                // Check the incoming connections of all nodes except
                // the first node in the array
                if (index > 0) {
                    const incomingEdges = pifFileSummary.incomingEdgesMap[refNode.name];
                    incomingEdges?.forEach((incomingEdge) => {
                        // If the group name for this incoming edge is not the same
                        // as the group name for the node that we are checking, then
                        // raise an error.
                        const { idField: incomingEdgeIdField, groupNameField } = incomingEdge;
                        if (groupNameField?.groupName?.value !== groupName) {
                            accept('error', `Nodes other than the first node in a node group cannot be routed to from outside the group. Node '${incomingEdge.refNode.name}' is routing to node '${refNode.name}' in group '${groupName}'.`, {
                                node: incomingEdge.refNode,
                                data: {
                                    id: incomingEdgeIdField?.id?.value,
                                    nodeNumber: incomingEdge.refNode.name,
                                },
                            });
                        }
                    });
                }
                // Check the outgoing connections of all nodes except
                // the last node in the array
                if (index < nodeFieldsArray.length - 1) {
                    const outgoingEdges = pifFileSummary.outgoingEdgesMap[refNode.name];
                    outgoingEdges?.forEach((outgoingEdge) => {
                        // If the group name for this outgoing edge is not the same
                        // as the group name for the node that we are checking, then
                        // raise an error.
                        const { groupNameField } = outgoingEdge;
                        if (groupNameField?.groupName?.value !== groupName) {
                            accept('error', `Nodes in a node group, other than the final node, cannot route outside the group. Node '${refNode.name}' routes to node '${outgoingEdge.refNode.name}' which is outside of group '${groupName}'.`, {
                                node: refNode,
                                data: {
                                    id: idField?.id?.value,
                                    nodeNumber: refNode.name,
                                },
                            });
                        }
                    });
                }
            });
        });
    }
}
//# sourceMappingURL=json-file.js.map